import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    PiCoinsDuotone,
    PiCrownDuotone,
    PiShoppingCartDuotone,
    PiTrashDuotone,
} from 'react-icons/pi';

import DetailModal from 'components/DetailModal';
import GenericAuthModal from 'components/GenericAuthModal';
import ShopBasketModal from 'components/ShopBasketModal';
import PageHeading from 'elements/PageHeading';
import Pattern from 'elements/Pattern';
import { useAuth } from 'providers/AuthProvider';
import { useShop } from 'providers/ShopProvider';
import { getColorConfig } from 'utils/StyleUtils';

export default function ShopPage({ className = '' }) {
    const [filterVip, setFilterVip] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [basketOpen, setBasketOpen] = useState(false);
    const [resumeCheckout, setResumeCheckout] = useState(false);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const { userData, authChecked } = useAuth();
    const {
        shopItems,
        shopError,
        basket,
        basketPointTotal,
        addToBasket,
        removeFromBasket,
        isInBasket,
        getShopItemColorByIdAndName,
    } = useShop();

    const filteredItems = useMemo(() => {
        if (!shopItems) return [];

        return [...shopItems]
            .filter((item) => !filterVip || item.vip)
            .sort((a, b) => Number(a.pointsPrice || 0) - Number(b.pointsPrice || 0));
    }, [filterVip, shopItems]);

    useEffect(() => {
        if (!location.state?.resumeCheckout || !userData || shopItems === null) return;

        setResumeCheckout(true);
        setBasketOpen(true);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.pathname, location.state, navigate, shopItems, userData]);

    if (!authChecked) {
        return <ShopLoading />;
    }

    return (
        <div className={`relative flex w-full flex-1 flex-col items-center overflow-hidden gradient-green px-site py-site ${className}`}>
            <Pattern className="pointer-events-none opacity-70" />

            <div className="relative z-10 flex w-full max-w-boxed flex-col gap-10 md:gap-14">
                <div className="flex w-full flex-col gap-8">
                    <PageHeading
                        title="Fürge Club – Több jár neked!"
                        align="left"
                        mobileAlign="center"
                        subTitle={(
                            <span className="flex flex-col gap-2 text-sm leading-relaxed text-white/85 sm:text-base">
                                <span>A munkádért kapott pontokat egyedi Fürge Diák termékekre, kedvezményekre és meglepetésekre válthatod be.</span>
                                <Link
                                    to="/assets/files/furge-club-szabalyzat.pdf"
                                    target="_blank"
                                    className="w-fit text-xs font-semibold text-white/60 underline decoration-white/25 underline-offset-4 hover:text-white/85"
                                >
                                    A pontgyűjtés szabályai
                                </Link>
                            </span>
                        )}
                    />

                    <div className="mx-auto grid w-full max-w-4xl grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <ShopSummary
                            userData={userData}
                            basketCount={basket.length}
                            basketPointTotal={basketPointTotal}
                            onOpenBasket={() => setBasketOpen(true)}
                        />

                        <VipFilterToggle value={filterVip} onChange={setFilterVip} />
                    </div>
                </div>

                {shopError && (
                    <div className="rounded-2xl bg-red-50 px-5 py-4 font-semibold text-red-700 shadow-lg">{shopError}</div>
                )}

                <ProductGrid
                    loading={shopItems === null}
                    items={filteredItems}
                    isInBasket={isInBasket}
                    onOpen={setSelectedItem}
                    onAdd={addToBasket}
                    onRemove={removeFromBasket}
                    getColor={getShopItemColorByIdAndName}
                />
            </div>

            <DetailModal
                isOpen={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                onOpenBasket={() => {
                    setSelectedItem(null);
                    setBasketOpen(true);
                }}
                item={selectedItem}
                color={selectedItem ? getShopItemColorByIdAndName(selectedItem.id, selectedItem.name) : null}
            />

            <ShopBasketModal
                open={basketOpen}
                resumeCheckout={resumeCheckout}
                onClose={() => {
                    setBasketOpen(false);
                    setResumeCheckout(false);
                }}
                onRequireLogin={() => setAuthModalOpen(true)}
            />

            <GenericAuthModal
                isOpen={authModalOpen}
                onClose={() => setAuthModalOpen(false)}
                compact
                loginState={{ returnTo: '/shop', resumeCheckout: true }}
            />
        </div>
    );
}

function VipFilterToggle({ value, onChange }) {
    return (
        <div className="flex min-h-20 rounded-2xl bg-black/15 p-1 shadow-inner" role="group" aria-label="Termékek szűrése">
            <button
                type="button"
                aria-pressed={!value}
                onClick={() => onChange(false)}
                className={`flex flex-1 items-center justify-center rounded-lg px-3 py-2 text-xs font-black transition ${!value ? 'bg-white text-neutral-800 shadow-md' : 'text-white/70 hover:text-white'}`}
            >
                Összes termék
            </button>
            <button
                type="button"
                aria-pressed={value}
                onClick={() => onChange(true)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black transition ${value ? 'gradient-yellow text-white shadow-md' : 'text-white/70 hover:text-white'}`}
            >
                <PiCrownDuotone className="size-4" />
                Csak VIP
            </button>
        </div>
    );
}

function ShopSummary({ userData, basketCount, basketPointTotal, onOpenBasket }) {
    return (
        <>
            <div className="flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-neutral-900 shadow-2xl">
                <span className="gradient-yellow flex size-11 shrink-0 items-center justify-center rounded-xl text-white">
                    <PiCoinsDuotone className="size-7" />
                </span>
                <span>
                    <span className="block text-xs font-semibold text-neutral-500">Aktuális egyenleged</span>
                    <strong className="font-display text-2xl font-black">{userData ? `${userData.credit ?? 0} pont` : 'Jelentkezz be'}</strong>
                </span>
            </div>

            <button
                type="button"
                onClick={onOpenBasket}
                className="gradient-blue group relative flex w-full items-center gap-3 rounded-2xl p-4 text-left text-white shadow-2xl transition hover:-translate-y-0.5 hover:brightness-110"
            >
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
                    <PiShoppingCartDuotone className="size-7" />
                </span>
                <span>
                    <span className="block text-xs font-semibold text-white/65">Kosaram</span>
                    <strong className="font-display text-xl font-black">{basketCount} termék · {basketPointTotal} pont</strong>
                </span>
                {basketCount > 0 && <span className="gradient-red absolute -right-2 -top-2 flex size-8 items-center justify-center rounded-full text-xs font-black shadow-lg">{basketCount}</span>}
            </button>
        </>
    );
}

function ProductGrid({ loading, items, isInBasket, onOpen, onAdd, onRemove, getColor }) {
    if (loading) {
        return (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {[...Array(8)].map((_, index) => <div key={index} className="aspect-[4/5] animate-pulse rounded-3xl bg-white/20" />)}
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="rounded-3xl bg-white p-8 text-center text-neutral-700 shadow-2xl">
                <PiShoppingCartDuotone className="mx-auto mb-3 size-12 text-neutral-300" />
                <p className="font-display text-xl font-black">Jelenleg nincs megjeleníthető termék.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => (
                <ProductCard
                    key={item.id}
                    item={item}
                    color={getColor(item.id, item.name)}
                    inBasket={isInBasket(item.id)}
                    onOpen={() => onOpen(item)}
                    onToggle={() => isInBasket(item.id) ? onRemove(item.id) : onAdd(item.id)}
                />
            ))}
        </div>
    );
}

function ProductCard({ item, color, inBasket, onOpen, onToggle }) {
    const colorConfig = getColorConfig(color) || getColorConfig('blue');

    return (
        <article className="group flex overflow-hidden rounded-3xl bg-white shadow-2xl transition hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(0,0,0,0.25)]">
            <div className="flex w-full flex-col">
                <button type="button" onClick={onOpen} className="relative block aspect-square w-full overflow-hidden text-left">
                    <img
                        src={`${global.backingsEndpoint}photoRepository/getImageBySize?size=ORIGINAL&key=${item.photo}`}
                        alt={item.name}
                        className="size-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
                    {item.vip && (
                        <span className="gradient-yellow absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black text-white shadow-lg">
                            <PiCrownDuotone className="size-4" /> VIP termék
                        </span>
                    )}
                    <span className={`absolute bottom-3 right-3 rounded-xl px-3 py-2 font-display text-sm font-black text-white shadow-lg ${colorConfig.gradient}`}>
                        {item.pointsPrice} pont
                    </span>
                </button>

                <div className="flex flex-1 flex-col gap-4 p-4">
                    <button type="button" onClick={onOpen} className="text-left">
                        <h2 className="font-display text-xl font-black leading-tight text-neutral-900">{item.name}</h2>
                        {item.description && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-neutral-500">{item.description}</p>}
                    </button>

                    <button
                        type="button"
                        onClick={onToggle}
                        className={`${inBasket ? 'gradient-red' : colorConfig.gradient} mt-auto flex w-full items-center justify-center gap-2 rounded-xl py-3 font-black text-white shadow-lg transition hover:brightness-110 hover:shadow-xl`}
                    >
                        {inBasket ? <PiTrashDuotone className="size-5" /> : <PiShoppingCartDuotone className="size-5" />}
                        {inBasket ? 'Kiveszem' : 'Kosárba teszem'}
                    </button>
                </div>
            </div>
        </article>
    );
}

function ShopLoading() {
    return (
        <div className="flex min-h-[520px] w-full flex-1 items-center justify-center gradient-green">
            <div className="flex gap-2">
                <span className="size-4 animate-bounce rounded-full gradient-red" />
                <span className="size-4 animate-bounce rounded-full gradient-yellow [animation-delay:150ms]" />
                <span className="size-4 animate-bounce rounded-full gradient-blue [animation-delay:300ms]" />
            </div>
        </div>
    );
}
